import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Mail,
  Phone
} from 'lucide-react';
import WiseRankLogo from '../assets/logos/wiserank-logo.svg';

export const Footer = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Helper function to handle navigation
  const handleNavigation = (href: string) => {
    if (href.startsWith('/#')) {
      // If we're not on the home page and trying to navigate to a section
      if (location.pathname !== '/') {
        // First navigate to home page
        window.location.href = href;
      } else {
        // If we're already on home page, just scroll to the section
        const element = document.getElementById(href.substring(2));
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-white dark:bg-black border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <Link to="/" className="block">
              <img 
                src={WiseRankLogo} 
                alt="WiseRank.ai" 
                className="h-8 w-auto dark:invert" 
              />
            </Link>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
              {t('footer.quickLinks.title')}
            </h3>
            <ul className="space-y-3">
              {t('footer.quickLinks.links', { returnObjects: true }).map((link: any, index: number) => (
                <li key={index}>
                  {link.href.startsWith('/#') ? (
                    <button
                      onClick={() => handleNavigation(link.href)}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors hover-underline inline-block"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors hover-underline inline-block"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
              {t('footer.contact.title')}
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:hello@wiserank.ai"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center gap-2"
                >
                  <Mail size={16} />
                  hello@wiserank.ai
                </a>
              </li>
              <li>
                <a
                  href="tel:+33744958400"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center gap-2"
                >
                  <Phone size={16} />
                  +33 7 44 95 84 00
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              © {new Date().getFullYear()} WiseRank.ai. {t('footer.rights')}
            </p>
            <div className="flex items-center space-x-6">
              {t('footer.legal', { returnObjects: true }).map((link: any, index: number) => (
                <Link
                  key={index}
                  to={link.href}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors hover-underline"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}